/**
 * Design tokens for StudioVault, ported from the "StudioVault Screens" design
 * project (claude.ai/design).
 *
 * One source of truth. `palette.ts` / `typography.ts` / `shadows.ts` are
 * adapters that map these onto the MUI theme shape; nothing else should define
 * a raw colour.
 *
 * ## Reading the two modes
 *
 * `tokens(mode)` returns one shape for either mode, so callers never branch on
 * `isDark`. The naming is by **role**, not by appearance, because the two modes
 * invert: the accent used for *text* is lighter than the fill in dark mode and
 * darker than the fill in light mode. `accentText` is correct in both;
 * "accentBright" would only be true of one.
 *
 * Values marked `[derived]` were not specified by the design — the design only
 * defined the light values that screens 04 and 11 actually needed. They follow
 * the design's own rules (dark's `rgba(255,255,255,α)` fills become
 * `rgba(12,14,18,α)`; shadows lose roughly two thirds of their opacity) and are
 * inert until something renders them. Check one in context before relying on it.
 */

// ---------------------------------------------------------------------------
// Mode-independent
// ---------------------------------------------------------------------------

/** Typeface roles. */
export const fonts = {
  /** Headlines and card titles. */
  display: "'Oxanium Variable', 'Arial Narrow', system-ui, sans-serif",
  /** Body, labels, buttons. */
  ui: 'Manrope, system-ui, sans-serif',
  /** Metadata, counts, timestamps, status text. */
  mono: "'JetBrains Mono', ui-monospace, monospace",
} as const;

/**
 * Corner radii. `shape.ts` maps its small/medium/large onto this scale, so
 * there is one ladder rather than two.
 */
export const radii = {
  xs: 2,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 10,
  '2xl': 12,
  '3xl': 16,
  pill: 999,
} as const;

/** DAW icons, mapped to assets already in /public. */
export const dawLogos: Record<string, string> = {
  Ableton: '/dawLogos/icon/Ableton_icon.png',
  Cubase: '/dawLogos/icon/Cubase_icon.png',
  FLStudio: '/dawLogos/icon/FLStudio_icon.png',
  LogicPro: '/dawLogos/icon/LogicPro_icon.png',
  ProTools: '/dawLogos/icon/ProTools_icon.png',
  Reaper: '/dawLogos/icon/Reaper_icon.png',
  StudioOne: '/dawLogos/icon/StudioOne_icon.png',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ThemeMode = 'light' | 'dark';

/**
 * Every state chip, dot, timeline node and sync row draws from exactly one of
 * these, which is what makes the surfaces read as a single system.
 */
export type StatusKey =
  | 'synced'
  | 'syncing'
  | 'behind'
  | 'conflict'
  | 'neutral'
  | 'stems';

export interface StatusToken {
  fg: string;
  bg: string;
  border: string;
}

export interface ColorTokens {
  /** What a filled accent control is painted with. Carries `onAccent`. */
  accentFill: string;
  accentFillHover: string;
  /** Accent used as text or an icon on this mode's surface. */
  accentText: string;
  /** Text/icon colour that sits on top of a filled accent surface. */
  onAccent: string;
  cyan: string;
  cyanLight: string;
  onCyan: string;
  green: string;
  onGreen: string;
  amber: string;
  red: string;
}

export interface SurfaceTokens {
  backdrop: string;
  /** Page canvas behind everything. */
  canvas: string;
  /** Screen shell — app frame, boards, detail panes. */
  screen: string;
  /** Solid panel — sidebar, auth cards. */
  panel: string;
  /** Ring colour for avatars overlapping a card footer. */
  avatarRing: string;
  /** Opaque modal / drawer surfaces. */
  glass: string;
  glassStrong: string;
  glassDrawer: string;
  glassHeader: string;
  glassMenu: string;
  /** Flat translucent fills stacked on any of the above. */
  fillFaint: string;
  fillSubtle: string;
  fillWeak: string;
  fill: string;
  fillStrong: string;
  fillHover: string;
  fillActive: string;
  /** One step past the ladder — the hover state of a secondary button. */
  fillHeavy: string;
  /** Raised card — the top-lit gradient used by project/board cards. */
  cardGradient: string;
  cardGradientSoft: string;
  cardGradientGlass: string;
  /** Neutral placeholder behind a DAW icon while it loads. */
  iconWell: string;
  /** Progress / meter track. */
  track: string;
  trackSoft: string;
}

/** Five steps, primary to faintest. */
export interface TextTokens {
  primary: string;
  /** One step below primary — nested rows, secondary list items. */
  bright: string;
  secondary: string;
  muted: string;
  faint: string;
}

/** Hairlines, weakest to strongest. */
export interface BorderTokens {
  hairline: string;
  divider: string;
  subtle: string;
  default: string;
  medium: string;
  card: string;
  strong: string;
  stronger: string;
  control: string;
  dashed: string;
  /** Hover target for a card that can receive focus or a drag. */
  accentHover: string;
}

export interface ShadowTokens {
  /** Full-bleed screen shell. */
  screen: string;
  /** Modal / drawer, with a top inner highlight for the glass edge. */
  modal: string;
  drawer: string;
  card: string;
  cardSoft: string;
  cardFlat: string;
  /** Card lifted by an active drag. */
  dragging: string;
  menu: string;
  panel: string;
  /** Recessed controls and display wells. */
  controlInset: string;
  /** Tactile control at rest; the highlight assumes a top-left light source. */
  button: string;
  /** Tactile control while pressed. */
  buttonPressed: string;
  /** Glow under a filled accent control. */
  accent: string;
  accentMd: string;
  accentLg: string;
  accentXl: string;
  /** Focus ring on a selected input or card. */
  focusRing: string;
  selectedRing: string;
}

export interface AvatarTone {
  bg: string;
  fg: string;
}

export interface AvatarToneTokens {
  accent: AvatarTone;
  cyan: AvatarTone;
  green: AvatarTone;
  neutral: AvatarTone;
  muted: AvatarTone;
}

export interface GradientTokens {
  logoMark: string;
  accentBar: string;
  cyanBar: string;
  heroGlow: string;
  stepRule: string;
}

export interface ModeTokens {
  colors: ColorTokens;
  surfaces: SurfaceTokens;
  text: TextTokens;
  borders: BorderTokens;
  status: Record<StatusKey, StatusToken>;
  avatarTones: AvatarToneTokens;
  shadows: ShadowTokens;
  gradients: GradientTokens;
}

// ---------------------------------------------------------------------------
// Dark
// ---------------------------------------------------------------------------

const darkColors: ColorTokens = {
  accentFill: '#67B9E7',
  accentFillHover: '#82C9EF',
  accentText: '#8ACDF2',
  onAccent: '#071217',
  cyan: '#5EC4EE',
  cyanLight: '#91DBF7',
  onCyan: '#06171D',
  green: '#79D66B',
  onGreen: '#0A1D08',
  amber: '#F0D86E',
  red: '#EF6A55',
};

const darkText: TextTokens = {
  // Warm, low-glare neutrals keep long sessions comfortable without looking blue-gray.
  primary: '#E2DED6',
  bright: '#CEC9C0',
  secondary: '#ACA69D',
  muted: '#817C75',
  faint: '#5F5B56',
};

const darkSurfaces: SurfaceTokens = {
  backdrop: 'rgba(5,6,6,0.68)',
  canvas: '#1C1C1B',
  screen: '#242423',
  panel: '#30302E',
  avatarRing: '#383735',
  glass: '#30302E',
  glassStrong: '#383735',
  glassDrawer: '#242423',
  glassHeader: '#292928',
  glassMenu: '#383735',
  fillFaint: 'rgba(226,222,214,0.018)',
  fillSubtle: 'rgba(226,222,214,0.03)',
  fillWeak: 'rgba(226,222,214,0.045)',
  fill: 'rgba(226,222,214,0.055)',
  fillStrong: 'rgba(226,222,214,0.075)',
  fillHover: 'rgba(226,222,214,0.1)',
  fillActive: 'rgba(226,222,214,0.125)',
  fillHeavy: 'rgba(226,222,214,0.18)',
  cardGradient: '#3D3C39',
  cardGradientSoft: '#373633',
  cardGradientGlass: '#373633',
  iconWell: '#292927',
  track: '#20201F',
  trackSoft: '#292927',
};

const darkBorders: BorderTokens = {
  hairline: 'rgba(226,222,214,0.055)',
  divider: 'rgba(226,222,214,0.07)',
  subtle: 'rgba(226,222,214,0.085)',
  default: 'rgba(226,222,214,0.1)',
  medium: 'rgba(226,222,214,0.115)',
  card: 'rgba(226,222,214,0.125)',
  strong: 'rgba(226,222,214,0.14)',
  stronger: 'rgba(226,222,214,0.16)',
  control: 'rgba(226,222,214,0.18)',
  dashed: 'rgba(226,222,214,0.15)',
  accentHover: 'rgba(103,185,231,0.5)',
};

const darkStatus: Record<StatusKey, StatusToken> = {
  synced: {
    fg: darkColors.green,
    bg: 'rgba(62,207,142,0.12)',
    border: 'rgba(62,207,142,0.3)',
  },
  syncing: {
    fg: darkColors.accentText,
    bg: 'rgba(103,185,231,0.14)',
    border: 'rgba(103,185,231,0.36)',
  },
  behind: {
    fg: darkColors.amber,
    bg: 'rgba(245,196,81,0.12)',
    border: 'rgba(245,196,81,0.3)',
  },
  conflict: {
    fg: darkColors.red,
    bg: 'rgba(255,92,92,0.12)',
    border: 'rgba(255,92,92,0.32)',
  },
  neutral: {
    fg: darkText.secondary,
    bg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
  },
  stems: {
    fg: darkColors.cyan,
    bg: 'rgba(85,200,232,0.12)',
    border: 'rgba(85,200,232,0.32)',
  },
};

const dark: ModeTokens = {
  colors: darkColors,
  surfaces: darkSurfaces,
  text: darkText,
  borders: darkBorders,
  status: darkStatus,
  avatarTones: {
    accent: { bg: darkColors.accentFill, fg: darkColors.onAccent },
    cyan: { bg: darkColors.cyan, fg: darkColors.onCyan },
    green: { bg: darkColors.green, fg: darkColors.onGreen },
    neutral: { bg: 'rgba(226,222,214,0.1)', fg: darkText.secondary },
    muted: { bg: 'rgba(226,222,214,0.08)', fg: darkText.muted },
  },
  shadows: {
    screen:
      'inset 0 1px 0 rgba(242,236,224,0.075),inset 1px 0 0 rgba(242,236,224,0.025),0 1px 1px rgba(0,0,0,0.56),8px 20px 40px -22px rgba(0,0,0,0.78)',
    modal:
      'inset 0 1px 0 rgba(242,236,224,0.09),inset 1px 0 0 rgba(242,236,224,0.03),0 1px 1px rgba(0,0,0,0.6),10px 20px 34px -18px rgba(0,0,0,0.82)',
    drawer:
      'inset 1px 0 0 rgba(242,236,224,0.04),6px 8px 24px -14px rgba(0,0,0,0.78)',
    card: 'inset 0 1px 0 rgba(242,236,224,0.065),inset 1px 0 0 rgba(242,236,224,0.02),0 1px 1px rgba(0,0,0,0.5),4px 8px 14px -9px rgba(0,0,0,0.72)',
    cardSoft:
      'inset 0 1px 0 rgba(242,236,224,0.05),0 1px 1px rgba(0,0,0,0.44),2px 5px 10px -7px rgba(0,0,0,0.66)',
    cardFlat:
      'inset 0 1px 0 rgba(242,236,224,0.045),0 1px 1px rgba(0,0,0,0.42),2px 4px 8px -7px rgba(0,0,0,0.58)',
    dragging:
      'inset 0 1px 0 rgba(242,236,224,0.09),0 2px 1px rgba(0,0,0,0.58),10px 18px 28px -12px rgba(0,0,0,0.82)',
    menu: 'inset 0 1px 0 rgba(242,236,224,0.075),0 1px 1px rgba(0,0,0,0.56),7px 14px 24px -12px rgba(0,0,0,0.8)',
    panel:
      'inset 0 1px 0 rgba(242,236,224,0.06),inset 1px 0 0 rgba(242,236,224,0.02),0 1px 1px rgba(0,0,0,0.5),5px 11px 20px -14px rgba(0,0,0,0.72)',
    controlInset:
      'inset 0 1px 2px rgba(0,0,0,0.16),0 1px 0 rgba(242,236,224,0.025)',
    button: 'inset 0 1px 0 rgba(255,255,255,0.09),0 1px 2px rgba(0,0,0,0.22)',
    buttonPressed:
      'inset 0 1px 2px rgba(0,0,0,0.3),inset 0 -1px 0 rgba(255,255,255,0.06)',
    accent:
      'inset 0 1px 0 rgba(255,255,255,0.24),inset 0 -1px 0 rgba(0,0,0,0.14),0 1px 1px rgba(0,0,0,0.46),2px 4px 8px -6px rgba(0,0,0,0.64)',
    accentMd:
      'inset 0 1px 0 rgba(255,255,255,0.24),inset 0 -1px 0 rgba(0,0,0,0.14),0 1px 1px rgba(0,0,0,0.46),3px 5px 9px -7px rgba(0,0,0,0.64)',
    accentLg:
      'inset 0 1px 0 rgba(255,255,255,0.24),inset 0 -1px 0 rgba(0,0,0,0.14),0 1px 1px rgba(0,0,0,0.46),4px 6px 11px -8px rgba(0,0,0,0.64)',
    accentXl:
      'inset 0 1px 0 rgba(255,255,255,0.24),inset 0 -1px 0 rgba(0,0,0,0.14),0 1px 1px rgba(0,0,0,0.46),5px 7px 13px -9px rgba(0,0,0,0.64)',
    focusRing: '0 0 0 3px rgba(103,185,231,0.2)',
    selectedRing: '0 0 0 3px rgba(103,185,231,0.16)',
  },
  gradients: {
    logoMark: '#67B9E7',
    accentBar: '#67B9E7',
    cyanBar: '#5EC4EE',
    heroGlow: 'none',
    stepRule: '#67B9E7',
  },
};

// ---------------------------------------------------------------------------
// Light
// ---------------------------------------------------------------------------

const lightColors: ColorTokens = {
  accentFill: '#F0560B',
  accentFillHover: '#D94A05',
  accentText: '#C74806',
  onAccent: '#FFFFFF',
  cyan: '#1E88A8',
  cyanLight: '#55C8E8', // [derived]
  onCyan: '#FFFFFF', // [derived]
  green: '#179E69',
  onGreen: '#FFFFFF', // [derived]
  amber: '#B47C0A',
  red: '#D33A3A',
};

const lightText: TextTokens = {
  primary: '#14161A',
  bright: '#2C3038',
  secondary: '#5A5F68',
  muted: '#8B9098',
  faint: '#B0B4BA',
};

const lightSurfaces: SurfaceTokens = {
  backdrop: 'rgba(12,14,18,0.35)',
  /** Warm off-white, not pure white, so cards can be #FFF. */
  canvas: '#F6F5F2',
  screen: '#F6F5F2', // [derived]
  panel: '#FFFFFF',
  avatarRing: '#FFFFFF',
  glass: 'rgba(255,255,255,0.86)', // [derived]
  glassStrong: 'rgba(255,255,255,0.9)', // [derived]
  glassDrawer: 'rgba(255,255,255,0.86)',
  glassHeader: 'rgba(255,255,255,0.8)',
  glassMenu: 'rgba(255,255,255,0.96)', // [derived]
  fillFaint: 'rgba(12,14,18,0.015)', // [derived]
  fillSubtle: 'rgba(12,14,18,0.025)', // [derived]
  fillWeak: 'rgba(12,14,18,0.03)', // [derived]
  fill: 'rgba(12,14,18,0.045)',
  fillStrong: 'rgba(12,14,18,0.05)',
  fillHover: 'rgba(12,14,18,0.06)', // [derived]
  fillActive: 'rgba(12,14,18,0.07)', // [derived]
  fillHeavy: 'rgba(12,14,18,0.12)', // [derived]
  cardGradient: '#FFFFFF',
  cardGradientSoft: '#FFFFFF',
  cardGradientGlass: '#FFFFFF',
  iconWell: '#EDEBE6',
  track: 'rgba(12,14,18,0.1)',
  trackSoft: 'rgba(12,14,18,0.11)', // [derived]
};

/**
 * The light ladder is offset from the dark one — the design's light `divider`
 * is 0.09 where dark's is 0.07 — so these are matched by the weight each rung
 * actually renders at, not by numeric equality.
 */
const lightBorders: BorderTokens = {
  hairline: 'rgba(12,14,18,0.06)', // [derived]
  divider: 'rgba(12,14,18,0.09)',
  subtle: 'rgba(12,14,18,0.08)',
  default: 'rgba(12,14,18,0.09)', // [derived]
  medium: 'rgba(12,14,18,0.1)',
  card: 'rgba(12,14,18,0.11)',
  strong: 'rgba(12,14,18,0.12)',
  stronger: 'rgba(12,14,18,0.13)', // [derived]
  control: 'rgba(12,14,18,0.14)',
  dashed: 'rgba(12,14,18,0.16)',
  accentHover: 'rgba(240,86,11,0.5)',
};

/** Alphas run lower than the dark set — a tint reads far louder on white. */
const lightStatus: Record<StatusKey, StatusToken> = {
  synced: {
    fg: lightColors.green,
    bg: 'rgba(23,158,105,0.1)',
    border: 'rgba(23,158,105,0.28)',
  },
  syncing: {
    fg: lightColors.accentText,
    bg: 'rgba(240,86,11,0.1)',
    border: 'rgba(240,86,11,0.3)',
  },
  behind: {
    fg: lightColors.amber,
    bg: 'rgba(180,124,10,0.1)',
    border: 'rgba(180,124,10,0.28)',
  },
  conflict: {
    fg: lightColors.red,
    bg: 'rgba(211,58,58,0.09)',
    border: 'rgba(211,58,58,0.26)',
  },
  neutral: {
    fg: lightText.secondary,
    bg: lightSurfaces.panel,
    border: lightBorders.control,
  },
  stems: {
    fg: lightColors.cyan,
    bg: 'rgba(30,136,168,0.1)',
    border: 'rgba(30,136,168,0.28)',
  },
};

const light: ModeTokens = {
  colors: lightColors,
  surfaces: lightSurfaces,
  text: lightText,
  borders: lightBorders,
  status: lightStatus,
  avatarTones: {
    /** Light avatars invert: saturated fill, white initials. */
    accent: { bg: lightColors.accentFill, fg: '#FFFFFF' },
    cyan: { bg: lightColors.cyan, fg: '#FFFFFF' },
    green: { bg: lightColors.green, fg: '#FFFFFF' },
    neutral: { bg: 'rgba(12,14,18,0.09)', fg: lightText.secondary },
    muted: { bg: 'rgba(12,14,18,0.07)', fg: lightText.muted }, // [derived]
  },
  /** Far shallower than dark — on white, dark-opacity shadows read as dirt. */
  shadows: {
    screen: '0 40px 90px -40px rgba(0,0,0,0.55)',
    modal:
      '0 40px 90px -30px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.9)', // [derived]
    drawer: '0 40px 90px -34px rgba(0,0,0,0.4)', // [derived]
    card: '0 10px 26px -18px rgba(0,0,0,0.35)',
    cardSoft: '0 10px 26px -20px rgba(0,0,0,0.3)', // [derived]
    cardFlat: '0 8px 20px -16px rgba(0,0,0,0.28)', // [derived]
    dragging: '0 24px 44px -16px rgba(0,0,0,0.45)', // [derived]
    menu: '0 24px 50px -20px rgba(0,0,0,0.4)', // [derived]
    panel: '0 12px 30px -20px rgba(0,0,0,0.4)',
    controlInset:
      'inset 1px 2px 3px rgba(12,14,18,0.12),inset 0 -1px 0 rgba(255,255,255,0.85)',
    button:
      'inset 0 1px 0 rgba(255,255,255,0.7),inset 0 -1px 0 rgba(12,14,18,0.12),0 2px 1px rgba(12,14,18,0.2),3px 5px 9px -6px rgba(12,14,18,0.35)',
    buttonPressed:
      'inset 1px 2px 3px rgba(12,14,18,0.18),inset 0 -1px 0 rgba(255,255,255,0.6)',
    accent: '0 8px 20px -12px rgba(240,86,11,0.9)',
    accentMd: '0 10px 26px -12px rgba(240,86,11,0.9)', // [derived]
    accentLg: '0 10px 26px -12px rgba(240,86,11,0.9)',
    accentXl: '0 14px 34px -14px rgba(240,86,11,0.9)', // [derived]
    focusRing: '0 0 0 3px rgba(240,86,11,0.18)', // [derived]
    selectedRing: '0 0 0 3px rgba(240,86,11,0.14)', // [derived]
  },
  gradients: {
    logoMark: '#F0560B',
    accentBar: '#F0560B',
    cyanBar: '#1E88A8',
    heroGlow: 'none',
    stepRule: '#F0560B',
  },
};

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const darkTokens = dark;
export const lightTokens = light;

/** The one accessor. Callers pick a mode once and never branch again. */
export const tokens = (mode: ThemeMode): ModeTokens =>
  mode === 'light' ? light : dark;

// ---------------------------------------------------------------------------
// Legacy accessors — /signal reference screens only
// ---------------------------------------------------------------------------

/**
 * The 17 `/signal/*` screens are a design reference, not a product surface, and
 * they carry ~1000 call sites against the old flat names. Renaming all of them
 * would be churn with real regression risk and no payoff, so they keep these
 * accessors, which are views onto the objects above rather than copies — there
 * is still exactly one definition of every value.
 *
 * New code should use `tokens(mode)`. These go when the reference screens do.
 */
export const signalColors = {
  ...darkColors,
  accent: darkColors.accentFill,
  accentHover: darkColors.accentFillHover,
  accentBright: darkColors.accentText,
} as const;

export const signalColorsOnLight = {
  ...lightColors,
  accent: lightColors.accentText,
  accentFill: lightColors.accentFill,
  accentFillHover: lightColors.accentFillHover,
  accentGradientStart: lightColors.accentFill,
  onAccent: lightColors.onAccent,
} as const;

export const signalSurfaces = darkSurfaces;
export const signalSurfacesLight = lightSurfaces;
export const signalText = darkText;
export const signalTextLight = {
  ...lightText,
  body: lightText.bright,
} as const;
export const signalBorders = darkBorders;
export const signalBordersLight = lightBorders;
export const signalStatus = darkStatus;
export const signalStatusLight = lightStatus;
export const signalAvatarTones = dark.avatarTones;
export const signalAvatarTonesLight = light.avatarTones;
export const signalShadows = dark.shadows;
export const signalShadowsLight = light.shadows;
export const signalFonts = fonts;
export const signalRadii = radii;
export const signalDawLogos = dawLogos;

export const signalGradients = {
  logoMark: dark.gradients.logoMark,
  logoMarkLight: light.gradients.logoMark,
  accentBar: dark.gradients.accentBar,
  accentBarLight: light.gradients.accentBar,
  cyanBar: dark.gradients.cyanBar,
  heroGlow: dark.gradients.heroGlow,
  stepRule: dark.gradients.stepRule,
} as const;

export type SignalStatusKey = StatusKey;
export type SignalStatusToken = StatusToken;
export type SignalAvatarTone = keyof AvatarToneTokens;
